import {useCallback, useEffect, useRef, useState} from 'react';

import {debounce} from 'lodash';

import {getLocalizationService} from '~/shared/services/localisationService';
import {createLogger} from '~/shared/logging';
import {trackEvent} from '~/shared/services/analytics';
import {GeolocationPlace, getCurrentGeolocationPlace} from '~/shared/services/geocoder';
import {Address, LocalAddress} from '~/shared/store/models';
import {AddressPartsFromKey} from '~/shared/utils/address';
import {AddressFinderVariant} from '~/shared/consts/addressConsts';
import {AddressFinderLocation, FormNamesEnum} from '~/shared/services/analytics/analyticsModels';

import getAddressFinderAddress from './getAddressFinderAddress';

const logger = createLogger('useAddressFinder');

const useAddressFinder = ({setDisableSubmit, onChange, location, submitForm, disableAutoFocus, disableSubmit, setCantFindAddressError, variant = AddressFinderVariant.LEGACY}: {
  location: AddressFinderLocation;
  submitForm: (address?: Address | LocalAddress | AddressPartsFromKey) => Promise<void>;
  onChange: (address?: Address | LocalAddress | AddressPartsFromKey) => void;
  setDisableSubmit: React.Dispatch<boolean>;
  disableAutoFocus: boolean;
  disableSubmit: boolean;
  variant?: AddressFinderVariant;
  setCantFindAddressError?: (value: boolean) => void;
}) => {
  const {t} = getLocalizationService();
  const [currentPlace, setCurrentPlace] = useState<GeolocationPlace | null>(null);
  const [customHouseNumber, setCustomHouseNumber] = useState();
  const [showAddressNumberInput, setShowAddressNumberInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  
  const isHouseNumberError = Boolean(showAddressNumberInput && errorMessage);
  const isAddressAutoCompleteError = Boolean(errorMessage) && !isHouseNumberError;

  const addressSelectorRef = useCallback((domEl: HTMLInputElement) => {
    if (domEl && !disableAutoFocus) {
      domEl.focus();
    }
  }, [disableAutoFocus]);

  const debouncedSetCustomHouseNumber = debounce(value => {
    setCustomHouseNumber(value);
  }, 1000);

  const onPlaceSelected = useCallback(
    async (place: GeolocationPlace) => {
      if (!place || !place.place_id) {
        setErrorMessage(t('unexpected_error'));
        if (setCantFindAddressError) {
          setCantFindAddressError(true);
        }
        return;
      }

      setCurrentPlace(place);

      const addressRes = await getAddressFinderAddress(customHouseNumber, place);
      if ('error' in addressRes) {
        const {error: addressError} = addressRes;
        
        if (addressError.type === 'missing') {
          setErrorMessage(t('missing_xxx_please_fill_xxx', {field: addressError.field ? t(addressError.field) : ''}));
          if (setCantFindAddressError) {
            setCantFindAddressError(true);
          }
          return;
        }

        if (addressError.type === 'unexpected') {
          setErrorMessage(t('unexpected_error'));
          if (setCantFindAddressError) {
            setCantFindAddressError(true);
          }
          return;
        }

        if (addressError.type === 'missing_houseNumber') {
          const missingHouseNumberErrorMessage = variant === AddressFinderVariant.PIE ? t('house_number_missing') : t('missing_xxx_please_fill_xxx', {field: addressError.field ? t(addressError.field) : ''});
          setDisableSubmit(true);
          setShowAddressNumberInput(true);
          setErrorMessage(missingHouseNumberErrorMessage);
          return;
        }

        logger.warn('unhandled error');
        return;
      }

      const {data: address} = addressRes;
      setErrorMessage('');
      if (setCantFindAddressError) {
        setCantFindAddressError(false);
      }
      setDisableSubmit(false);

      onChange(address);

      if (location) {
        trackEvent('hasModifiedAddress', {
          modifiedAddressType: 'add address',
          modifiedLocation: location,
        });
      }

      await submitForm(address);
    },
    [customHouseNumber, setDisableSubmit, onChange, location, submitForm, t, variant, setCantFindAddressError],
  );

  const resetAddress = useCallback(() => {
    setDisableSubmit(true);
    setErrorMessage('');
    setShowAddressNumberInput(false);
    setCurrentPlace(null);
    onChange();
    if (setCantFindAddressError) {
      setCantFindAddressError(false);
    }
  }, [onChange, setDisableSubmit, setCantFindAddressError]);

  const getCurrentLocation = useCallback(async ({setAddress}: {setAddress: (address: string) => void}) => {
    try {
      const place = await getCurrentGeolocationPlace();

      resetAddress();
      setAddress(place.name);
      await onPlaceSelected(place);

      trackEvent('addressStartSearch', {
        searchOption: 'location_based',
        addressBarType: location,
      });
    } catch (err) {
      // ignore- failing getting the initial device address will just leave the address input empty.
      logger.warn('Cant get initial location', err);
    }
  }, [location, onPlaceSelected, resetAddress]);

  const handleCustomHouseNumberChange = useCallback((value: string) => {
    debouncedSetCustomHouseNumber(value);
  }, [debouncedSetCustomHouseNumber]);

  const removeValidationError = useCallback(() => {
    setErrorMessage('');
    if (setCantFindAddressError) {
      setCantFindAddressError(false);
    }
  }, [setCantFindAddressError]);

  useEffect(() => {
    if (!errorMessage) {
      return;
    }

    trackEvent('formErrorMessageView', {
      errorType: errorMessage,
      formName: FormNamesEnum.ADDRESS,
    });
  }, [errorMessage]);

  useEffect(() => {
    const keyEventsHandler = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !disableSubmit) {
        submitForm();
      }
    };
    rootRef.current?.addEventListener('keydown', keyEventsHandler, false);

    const refForCleanup = rootRef.current;
    return () => refForCleanup?.removeEventListener('keydown', keyEventsHandler, false);
  }, [addressSelectorRef, disableSubmit, rootRef, submitForm]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      if (currentPlace) {
        onPlaceSelected(currentPlace);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customHouseNumber]);

  return {
    errorMessage,
    showAddressNumberInput,
    rootRef,
    onPlaceSelected,
    resetAddress,
    getCurrentLocation,
    handleCustomHouseNumberChange,
    addressSelectorRef,
    removeValidationError,
    isHouseNumberError,
    isAddressAutoCompleteError,
  };
};

export default useAddressFinder;
